import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GET, handleApiError } from '@/shared/api/requests';
import { Loader2 } from 'lucide-react';

interface SharedHotspotData {
  id: number;
  hotspot: number;
  project: number;
  photo: {
    id: number;
    name: string;
    signed_url: string;
  };
  hotspot_detail: {
    id: number;
    title: string;
    pitch: number;
    yaw: number;
    hfov: number;
    overlay_photos: Array<{
      id: number;
      title: string;
      gcs_url: string;
      index: number;
    }>;
  };
  hotspot_info: {
    id: number;
    title: string;
    photo_id: number;
    photo_name: string;
    project_name: string;
  };
  type: string;
  trade: string;
  due_date: string | null;
  priority: string;
  location: string;
  description: string;
  shared_by_name: string;
  shared_to_name: string;
  created_at: string;
  viewed_at: string | null;
}

const SharedHotspotViewer: React.FC = () => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      if (!accessToken) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const response = await GET<SharedHotspotData>(`/shared-hotspot/${accessToken}/`);
        const shareData = response.data;

        // Build the interactive viewer URL with deep link parameters
        const params = new URLSearchParams({
          photo: String(shareData.photo.id),
          hotspot: String(shareData.hotspot_detail.id),
          pitch: String(shareData.hotspot_detail.pitch),
          yaw: String(shareData.hotspot_detail.yaw),
        });

        // Navigate to the project's interactive viewer with the deep link
        const viewerUrl = `/app/property/${shareData.project}/asbuilds?${params.toString()}`;
        navigate(viewerUrl, { replace: true });
      } catch (err) {
        console.error('Failed to load shared hotspot:', err);
        setError(handleApiError(err));
        setLoading(false);
      }
    };

    fetchAndRedirect();
  }, [accessToken, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading shared hotspot...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-foreground mb-4">Unable to Load Shared Hotspot</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default SharedHotspotViewer;

